import { useEffect, useRef, useState, type ComponentProps, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { loadSheet, sheetPanel } from '@/components/lazyUi';
import { refocus } from '@/components/sheetFocus';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { haptic } from '@/platform';
import { useT } from '@/str';
import { useZoom, ZOOMED } from '@/textSize';

type Panel = typeof import('@/components/SheetPanel').SheetPanel;

export type SheetProps = Omit<ComponentProps<Panel>, 'opener' | 'scrimShown'>;

interface Waiting {
  box: RefObject<HTMLDivElement | null>;
  opener: Element | null;
  close: () => void;
}

const waiting: Waiting[] = [];
let savedOverflow = '';

function onWaitingKey(e: KeyboardEvent) {
  const top = waiting[waiting.length - 1];
  if (!top) return;
  if (e.key === 'Tab') {
    e.preventDefault();
    (top.box.current?.querySelector('button') ?? top.box.current)?.focus({ preventScroll: true });
  } else if (e.key === 'Escape') {
    e.preventDefault();
    top.close();
  }
}

function hold(entry: Waiting) {
  if (waiting.length === 0) {
    const page = document.documentElement;
    savedOverflow = page.style.overflow;
    page.style.overflow = 'hidden';
    document.getElementById('root')?.setAttribute('inert', '');
    document.addEventListener('keydown', onWaitingKey, true);
  }
  waiting.push(entry);
}

function release(entry: Waiting) {
  const at = waiting.indexOf(entry);
  if (at >= 0) waiting.splice(at, 1);
  if (waiting.length > 0) return;
  document.removeEventListener('keydown', onWaitingKey, true);
  document.getElementById('root')?.removeAttribute('inert');
  document.documentElement.style.overflow = savedOverflow;
}

function currentOpener(): Element | null {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  const owner = waiting.find((w) => w.box.current?.contains(active));
  return owner ? owner.opener : active;
}

const BOTTOM_PAD =
  'pb-[calc(24px+max(env(safe-area-inset-bottom),var(--tg-viewport-safe-area-inset-bottom,0px))/var(--zoom,1))]';

export function Sheet(props: SheetProps) {
  const t = useT();
  const zoom = useZoom();
  const [Panel, setPanel] = useState<Panel | null>(() => sheetPanel());
  const [waited] = useState(() => Panel === null);
  const [failed, setFailed] = useState(false);
  const [opener] = useState<Element | null>(currentOpener);
  const box = useRef<HTMLDivElement>(null);
  const restore = useRef(false);
  const onClose = useRef(props.onClose);
  useEffect(() => {
    onClose.current = props.onClose;
  });

  useEffect(() => {
    if (Panel) return;
    let live = true;
    void loadSheet().then((loaded) => {
      if (!live) return;
      if (loaded) return setPanel(() => loaded);
      haptic.notify('error');
      setFailed(true);
      window.dispatchEvent(new Event('vite:preloadError'));
    });
    return () => {
      live = false;
    };
  }, [Panel]);

  useEffect(() => {
    if (Panel) return;
    const entry: Waiting = {
      box,
      opener,
      close: () => {
        restore.current = true;
        onClose.current();
      },
    };
    hold(entry);
    box.current?.focus({ preventScroll: true });
    return () => {
      release(entry);
      const below = waiting[waiting.length - 1];
      if (below) below.box.current?.focus({ preventScroll: true });
      else if (restore.current) refocus(opener);
    };
  }, [Panel, opener]);

  if (Panel) return <Panel {...props} opener={opener} scrimShown={waited} />;

  const name = failed
    ? t.errGeneric
    : (props.label ?? (typeof props.title === 'string' ? props.title : t.srLoading));

  return createPortal(
    <div
      ref={box}
      role="dialog"
      aria-modal="true"
      aria-busy={!failed || undefined}
      aria-label={name}
      data-state="open"
      tabIndex={-1}
      onClick={() => {
        restore.current = true;
        props.onClose();
      }}
      className="bg-scrim animate-scrim-in fixed inset-0 z-60 flex items-end justify-center outline-none"
    >
      {failed ? (
        <div
          role="alert"
          onClick={(e) => e.stopPropagation()}
          className="bg-background rounded-t-sheet border-bad w-full max-w-[600px] border-t-[3px]"
        >
          <div {...zoom} className={cn(ZOOMED, 'flex flex-col gap-3 px-4.5 pt-4', BOTTOM_PAD)}>
            <p className="text-t13 text-muted-foreground m-0 leading-[1.5] text-pretty">
              {t.errGeneric}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="bg-primary text-primary-foreground rounded-plate text-t15 min-h-13 w-full px-4 py-3.5 font-extrabold active:scale-[.98]"
            >
              {t.reloadApp}
            </button>
          </div>
        </div>
      ) : (
        <Spinner aria-hidden className="text-muted-foreground mb-[30vh] size-6" />
      )}
    </div>,
    document.body,
  );
}
